import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useClientManagement } from '../hooks/useClientManagement';
import { Client } from '../types';
import { useAI } from './AIContext';

interface ClientContextType {
  clients: Client[];
  activeClient: Client | null;
  loading: boolean;
  error: Error | null;
  setActiveClient: (client: Client | null) => void;
  fetchClientById: (id: string) => Promise<Client | null>;
  createClient: (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<Client | null>;
  updateClient: (clientId: string, updates: Partial<Client>) => Promise<boolean>;
  deleteClient: (clientId: string) => Promise<boolean>;
  getDeletePreview: (clientId: string) => Promise<any>;
  uploadClientLogo: (clientId: string, file: File) => Promise<string | null>;
  uploadClientDocument: (
    clientId: string,
    file: File,
    documentData: {
      title: string;
      type: 'press_release' | 'media_kit' | 'fact_sheet' | 'crisis_plan' | 'other';
    }
  ) => Promise<any>;
  refreshClients: () => Promise<void>;
}

export const ClientContext = createContext<ClientContextType | null>(null);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

interface ClientProviderProps {
  children: ReactNode;
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const {
    clients,
    currentClient,
    loading,
    error,
    fetchClients,
    fetchClient,
    createClient,
    updateClient,
    deleteClient,
    getDeletePreview,
    uploadClientLogo,
    uploadClientDocument,
    setCurrentClient // Raw setter from useClientManagement
  } = useClientManagement();

  const { setActiveClient: setAIActiveClient } = useAI();

  // New synchronized function to set active client in both contexts
  const synchronizedSetActiveClient = useCallback((client: Client | null) => {
    setCurrentClient(client); // Update client in ClientContext (via useClientManagement)
    setAIActiveClient(client); // Update client in AIContext
  }, [setCurrentClient, setAIActiveClient]);

  const refreshClientsList = useCallback(async () => {
    try {
      await fetchClients();
    } catch (err) {
      console.error("Error refreshing clients:", err);
      // Error will be set in fetchClients, we don't need to re-throw
    }
  }, [fetchClients]);

  // This function just fetches data, it doesn't set the client as active globally.
  // The component calling this should use setActiveClient if that's the desired behavior.
  const getClientById = useCallback(async (id: string) => {
    return await fetchClient(id);
  }, [fetchClient]);

  const contextValue: ClientContextType = {
    clients,
    activeClient: currentClient,
    loading,
    error,
    setActiveClient: synchronizedSetActiveClient, // Use the synchronized setter
    fetchClientById: getClientById,
    createClient,
    updateClient,
    deleteClient,
    getDeletePreview,
    uploadClientLogo,
    uploadClientDocument,
    refreshClients: refreshClientsList
  };

  return <ClientContext.Provider value={contextValue}>{children}</ClientContext.Provider>;
};
